import React, { useEffect, useMemo, useState } from 'react';
import { Equipment as EQUIPMENT_LIST, NEW_ALARM } from './constants';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space,
  Row,
  Col,
  Tag,
  Avatar,
  Typography,
  Switch,
  Card,
  Alert
} from 'antd';
import { 
  RocketOutlined, 
  ClockCircleOutlined,
  FileTextOutlined,
  UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTaskForm } from './hooks/useTaskForm';
import { calculateDuration } from './utils';
import PrioritySelector from './components/PrioritySelector';
import DifficultySelector from './components/DifficultySelector';
import DescriptionEditor from './components/DescriptionEditor';
import SubtaskManager from './components/SubtaskManager';
import FileUploader from './components/FileUploader';

const { Option } = Select;
const { Title } = Typography;

const TaskModalRefactored = ({ visible, onCancel, onSubmit, editingTask, users, equipmentOptions }) => {
  const [form] = Form.useForm();
  const [equipments, setEquipments] = useState([]);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [selectedAlarmId, setSelectedAlarmId] = useState(null);
  const [createFromAlarm, setCreateFromAlarm] = useState(false);
  
  const {
    formData,
    newSubtask,
    setNewSubtask,
    descriptionExpanded,
    descriptionMode,
    updateField,
    updateDateRange,
    addSubtask,
    deleteSubtask,
    toggleSubtask,
    editSubtask,
    clearCompletedSubtasks,
    markAllSubtasksComplete,
    markAllSubtasksIncomplete,
    addAttachment,
    removeAttachment,
    toggleDescriptionMode,
    toggleDescriptionExpanded,
    handleSubmit
  } = useTaskForm(editingTask, onSubmit);

  useEffect(() => {
    if (Array.isArray(equipmentOptions) && equipmentOptions.length > 0) {
      setEquipments(equipmentOptions);
      return;
    }
    if (Array.isArray(EQUIPMENT_LIST) && EQUIPMENT_LIST.length > 0) {
      const opts = EQUIPMENT_LIST.map(item => ({
        label: `${item.Title}${item?.model?.name ? ' • ' + item.model.name : ''}${item?.model?.category ? ' • ' + item.model.category : ''}`,
        value: item.ID,
        raw: item,
      }));
      setEquipments(opts);
      return;
    }
    setEquipments([]);
  }, [equipmentOptions]);

  // Reset alarm selection when modal opens/closes
  useEffect(() => {
    if (!visible) {
      setSelectedAlarm(null);
      setSelectedAlarmId(null);
      setCreateFromAlarm(false);
    }
  }, [visible]);

  const equipmentValueToLabel = useMemo(() => {
    const map = new Map();
    equipments.forEach(opt => map.set(opt.value, opt.label));
    return map;
  }, [equipments]);

  const handleFormSubmit = () => {
    form.validateFields().then(() => {
      handleSubmit();
    });
  };

  const handleDateChange = (dates) => {
    updateDateRange(dates);
  };

  const handleFileUpload = (info) => {
    if (info.file.status === 'done') {
      addAttachment(info.file);
    }
  };

  const handleDescriptionChange = (value) => {
    updateField('description', value);
  };

  const handleAlarmSelect = (alarmId) => {
    setSelectedAlarmId(alarmId);
    const alarm = NEW_ALARM.find(a => a.ID === alarmId);
    setSelectedAlarm(alarm);
    
    if (alarm) {
      // Auto-fill form data based on alarm
      let priority = 'Medium';
      let difficulty = 3;
      
      // Determine priority and difficulty based on alarm type and status
      if (alarm.Type === 'DISCONNECTED') {
        priority = 'Critical';
        difficulty = 5;
      } else if (alarm.Type === 'STATUS' && alarm.Status === 2) {
        priority = 'High';
        difficulty = 4;
      } else if (alarm.Type === 'STATUS') {
        priority = 'Medium';
        difficulty = 3;
      }
      
      // If Display contains "Insufficient", make it higher priority
      if (alarm.Display && alarm.Display.toLowerCase().includes('insufficient')) {
        priority = 'High';
        difficulty = 4;
      }
      
      // Set form values directly without triggering re-renders
      form.setFieldsValue({
        content: alarm.Display || alarm.Alarm_NAME,
        description: alarm.Description || alarm.Alarm_NAME,
        priority: priority,
        difficulty: difficulty,
        labels: ['alarm', 'maintenance', alarm.Type.toLowerCase(), `status-${alarm.Status}`]
      });
      
      // Update form data state
      updateField('content', alarm.Display || alarm.Alarm_NAME);
      updateField('description', alarm.Description || alarm.Alarm_NAME);
      updateField('priority', priority);
      updateField('difficulty', difficulty);
      
      // Create labels based on alarm data
      const labels = ['alarm', 'maintenance', alarm.Type.toLowerCase()];
      if (alarm.Status && alarm.Status !== '-') {
        labels.push(`status-${alarm.Status}`);
      }
      updateField('labels', labels);
      
      // Add equipment name as subtask if available
      if (alarm.Alarm_NAME && alarm.Alarm_NAME.includes('-')) {
        const equipmentName = alarm.Alarm_NAME.split('-').pop();
        const equipmentSubtask = {
          id: `alarm-equipment-${alarm.ID}`,
          content: `Check equipment: ${equipmentName}`,
          completed: false
        };
        addSubtask(equipmentSubtask);
      }
    }
  };

  const handleCreateFromAlarmToggle = (checked) => {
    setCreateFromAlarm(checked);
    if (!checked) {
      setSelectedAlarm(null);
      setSelectedAlarmId(null);
      // Reset form fields related to alarm
      form.setFieldsValue({
        content: '',
        description: '',
        priority: 'Medium',
        difficulty: 3,
        labels: []
      });
      // Also reset form data state
      updateField('content', '');
      updateField('description', '');
      updateField('priority', 'Medium');
      updateField('difficulty', 3);
      updateField('labels', []);
    }
  };

  const duration = calculateDuration(formData.startDate, formData.endDate);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <RocketOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
          <span>{editingTask ? 'Edit Task' : 'Create New Task'}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={950}
      style={{ top: 20 }}
      bodyStyle={{ 
        padding: '0',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}
    >
      {/* Compact Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '16px 24px',
        color: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ color: 'white', margin: 0 }}>
            {editingTask ? 'Update Task' : 'New Task'}
          </Title>
          {duration > 0 && (
            <Tag color="blue" style={{ border: 'none', fontWeight: '600' }}>
              <ClockCircleOutlined /> {duration} days
            </Tag>
          )}
        </div>
      </div>

      {/* Form Content */}
      <div style={{ padding: '24px' }}>
        {/* Alarm Selection Section */}
        <Card 
          size="small" 
          style={{ marginBottom: '20px', border: '1px solid #d9d9d9' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🚨 Create Task from Alarm</span>
              <Switch 
                checked={createFromAlarm}
                onChange={handleCreateFromAlarmToggle}
                size="small"
              />
            </div>
          }
        >
          {createFromAlarm && (
            <div>
              <Form.Item label="Select Alarm" style={{ marginBottom: '12px' }}>
                <Select
                  placeholder="Choose an alarm to create task from"
                  size="middle"
                  style={{ width: '100%' }}
                  showSearch={false}
                  onChange={handleAlarmSelect}
                  value={selectedAlarmId}
                  optionLabelProp="label"
                >
                  {NEW_ALARM.map(alarm => (
                    <Option 
                      key={alarm.ID} 
                      value={alarm.ID}
                      label={alarm.Display || alarm.Alarm_NAME}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {alarm.Display || alarm.Alarm_NAME}
                          </div>
                          {alarm.Description && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              {alarm.Description}
                            </div>
                          )}
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            <strong>Type:</strong> {alarm.Type}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <Tag color={alarm.Type === 'DISCONNECTED' ? 'red' : alarm.Type === 'STATUS' ? 'orange' : 'blue'}>
                            {alarm.Type}
                          </Tag>
                          {alarm.Status && alarm.Status !== '-' && (
                            <Tag color={alarm.Status === 2 ? 'red' : 'green'}>
                              Status: {alarm.Status}
                            </Tag>
                          )}
                          <div style={{ fontSize: '10px', color: '#999' }}>
                            ID: {alarm.ID}
                          </div>
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              {selectedAlarm && (
                <Alert
                  message={`Selected: ${selectedAlarm.Display || selectedAlarm.Alarm_NAME}`}
                  description={
                    <div>
                      {selectedAlarm.Description && <p><strong>Description:</strong> {selectedAlarm.Description}</p>}
                      <p><strong>Type:</strong> {selectedAlarm.Type}</p>
                      {selectedAlarm.Status && selectedAlarm.Status !== '-' && (
                        <p><strong>Status:</strong> {selectedAlarm.Status}</p>
                      )}
                      {/* <p><strong>Alarm ID:</strong> {selectedAlarm.ID}</p> */}
                      {selectedAlarm.Alarm_NAME && (
                        <p><strong>Alarm Name:</strong> {selectedAlarm.Alarm_NAME}</p>
                      )}
                    </div>
                  }
                  type={selectedAlarm.Type === 'DISCONNECTED' ? 'error' : selectedAlarm.Type === 'STATUS' ? 'warning' : 'info'}
                  showIcon
                  style={{ marginTop: '12px' }}
                />
              )}
            </div>
          )}
        </Card>

        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleFormSubmit}
          initialValues={formData}
        >
          {/* Title and Assignee */}
          <Row gutter={16} style={{ marginBottom: '20px' }}>
            <Col span={12}>
              <Form.Item 
                name="content" 
                label="Task Title" 
                rules={[{ required: true, message: 'Please enter a task title' }]}
                style={{ marginBottom: '12px' }}
              >
                <Input 
                  size="middle" 
                  placeholder="What needs to be done?"
                  prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                  style={{ borderRadius: '6px' }}
                  onChange={(e) => updateField('content', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="assignee" 
                label="Assign To" 
                rules={[{ required: true, message: 'Please select an assignee' }]}
                style={{ marginBottom: '12px' }}
              >
                <Select 
                  size="middle" 
                  placeholder="Select team member"
                  style={{ borderRadius: '6px' }}
                  dropdownStyle={{ borderRadius: '6px' }}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={(value) => updateField('assignee', value)}
                >
                  {users.map(user => (
                    <Option key={user} value={user}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar size="small" style={{ backgroundColor: '#87d068' }}>
                          {user.charAt(0)}
                        </Avatar>
                        <span>{user}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Equipment Selector */}
          <Row gutter={16} style={{ marginBottom: '20px' }}>
            <Col span={24}>
              <Form.Item name="equipmentIds" label="Equipments (Optional)" style={{ marginBottom: '12px' }}>
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Select equipments"
                  size="middle"
                  options={equipments}
                  optionFilterProp="label"
                  dropdownStyle={{ borderRadius: '6px' }}
                  style={{ borderRadius: '6px' }}
                  maxTagCount="responsive"
                  onChange={(value) => updateField('equipmentIds', value)}
                />
              </Form.Item>
              {Array.isArray(formData?.equipmentIds) && formData.equipmentIds.length > 0 ? (
                <Space wrap>
                  {formData.equipmentIds.map(id => (
                    <Tag key={id} color="geekblue">{equipmentValueToLabel.get(id) || id}</Tag>
                  ))}
                </Space>
              ) : null}
            </Col>
          </Row>

          {/* Description Section */}
          <div style={{ marginBottom: '20px' }}>
            <DescriptionEditor
              value={formData.description}
              onChange={handleDescriptionChange}
              mode={descriptionMode}
              expanded={descriptionExpanded}
              onModeChange={toggleDescriptionMode}
              onExpandedChange={toggleDescriptionExpanded}
            />
          </div>

          {/* Priority and Difficulty */}
          <Row gutter={16} style={{ marginBottom: '20px' }}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" style={{ marginBottom: '12px' }}>
                <PrioritySelector 
                  onChange={(value) => updateField('priority', value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="difficulty" label="Difficulty" style={{ marginBottom: '12px' }}>
                <DifficultySelector 
                  onChange={(value) => updateField('difficulty', value)}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Date Range and Labels */}
          <Row gutter={16} style={{ marginBottom: '20px' }}>
            <Col span={12}>
              <Form.Item 
                name="dateRange" 
                label="Time Frame"
                rules={[{ required: true, message: 'Please select dates!' }]}
                style={{ marginBottom: '12px' }}
              >
                <DatePicker.RangePicker 
                  size="middle"
                  style={{ width: '100%', borderRadius: '6px' }}
                  format="MMM DD, YYYY"
                  presets={[
                    { label: 'Today', value: [dayjs(), dayjs()] },
                    { label: 'This Week', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
                    { label: 'Next Week', value: [dayjs().add(1, 'week').startOf('week'), dayjs().add(1, 'week').endOf('week')] },
                  ]}
                  onChange={handleDateChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="labels" label="Labels" style={{ marginBottom: '12px' }}>
                <Select 
                  mode="tags" 
                  size="middle"
                  placeholder="Add labels (type and press enter)"
                  style={{ borderRadius: '6px' }}
                  dropdownStyle={{ borderRadius: '6px' }}
                  tokenSeparators={[',', ';']}
                  showSearch={false}
                  onChange={(value) => updateField('labels', value)}
                >
                  <Option value="urgent">🚨 Urgent</Option>
                  <Option value="maintenance">🔧 Maintenance</Option>
                  <Option value="bug">🐛 Bug</Option>
                  <Option value="feature">✨ Feature</Option>
                  <Option value="critical">🔴 Critical</Option>
                  <Option value="high-priority">🟠 High Priority</Option>
                  <Option value="medium-priority">🟡 Medium Priority</Option>
                  <Option value="low-priority">🟢 Low Priority</Option>
                  <Option value="electrical">⚡ Electrical</Option>
                  <Option value="mechanical">🔩 Mechanical</Option>
                  <Option value="sensor">📡 Sensor</Option>
                  <Option value="inverter">🔌 Inverter</Option>
                  <Option value="meter">📊 Meter</Option>
                  <Option value="relay">🔗 Relay</Option>
                  <Option value="rtu">💻 RTU</Option>
                  <Option value="weather">🌤️ Weather</Option>
                  <Option value="fault">⚠️ Fault</Option>
                  <Option value="disconnected">❌ Disconnected</Option>
                  <Option value="status">📈 Status</Option>
                  <Option value="alarm">🚨 Alarm</Option>
                  <Option value="repair">🔨 Repair</Option>
                  <Option value="inspection">🔍 Inspection</Option>
                  <Option value="testing">🧪 Testing</Option>
                  <Option value="calibration">⚙️ Calibration</Option>
                  <Option value="documentation">📚 Documentation</Option>
                  <Option value="review">👀 Review</Option>
                  <Option value="deployment">🚀 Deployment</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Subtasks Section */}
          <SubtaskManager
            subtasks={formData.subtasks}
            newSubtask={newSubtask}
            onNewSubtaskChange={setNewSubtask}
            onAddSubtask={addSubtask}
            onToggleSubtask={toggleSubtask}
            onDeleteSubtask={deleteSubtask}
            onEditSubtask={editSubtask}
            onClearCompleted={clearCompletedSubtasks}
            onMarkAllComplete={markAllSubtasksComplete}
            onMarkAllIncomplete={markAllSubtasksIncomplete}
          />

          {/* File Attachments Section */}
          <FileUploader
            attachments={formData.attachments}
            onFileUpload={handleFileUpload}
            onRemoveFile={removeAttachment}
          />

          {/* Action Buttons */}
          <div style={{ 
            paddingTop: '20px', 
            borderTop: '1px solid #f0f0f0',
            textAlign: 'right'
          }}>
            <Space>
              <Button 
                onClick={onCancel}
                style={{
                  borderRadius: '6px',
                  padding: '0 24px',
                  height: '40px',
                  fontSize: '14px'
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                style={{
                  background: 'linear-gradient(45deg, #1890ff, #722ed1)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0 32px',
                  height: '40px',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 2px 6px rgba(24, 144, 255, 0.3)'
                }}
              >
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </Space>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default TaskModalRefactored;